/**
 * Claude journey — protocol `mcp` (ADR-0159 §3).
 *
 * Discovery LIVE (agent card) → Read menu LIVE (ARD) → MCP `tools/list`
 * LIVE against `POST /v1/mcp/{site_key_hash}` (§5 safety envelope) →
 * MCP tool-call order illustration grounded in "this is how Claude
 * orders through connectors today — e.g. Square Cash App connector,
 * live since 2026-07-01" (grade-B, framed "documented by vendor:") →
 * mandate / policy / settlement + signed-audit illustration.
 *
 * The MCP phase renders REAL tool names when the live JSON-RPC response
 * carries a `result.tools[]` array — parsed opportunistically here so
 * the merchant sees their published skill set. Falls back to the honest
 * "(couldn't reach the surface: …)" copy when the fetch failed.
 *
 * The transactional phases are SKILL-AWARE: when the live tools/list
 * shows `place_order` is not exposed (skill disabled for this site),
 * the checkout illustration is replaced with the protocol-accurate
 * refusal (-32602 `unknown_tool`) + skipped payment/fulfillment rows —
 * the merchant sees their own governance working.
 */

import { toolNamesFromResult } from '../lib/facts.js';
import { buildDiscoveryPhase, buildReadMenuPhase } from './discovery.js';
import type { JourneyContext, Phase } from './types.js';

import type { LiveResult } from '../live/fetch-surface.js';

function summarizeToolsList(result: LiveResult): string {
  // json-first — the display body is capped at 900 chars and may not
  // parse (2026-07-05 field bug; see LiveResult.json).
  const names = toolNamesFromResult(result);
  const body = result.body;
  if (names === null) return body;
  if (names.length === 0) {
    return '0 tools exposed.';
  }
  const shown = names.slice(0, 6);
  const suffix = names.length > shown.length ? `\n  … (+${names.length - shown.length} more)` : '';
  return `${names.length} tool${names.length === 1 ? '' : 's'}:\n  ${shown.join('\n  ')}${suffix}`;
}

export function buildClaudeJourney(ctx: JourneyContext): Phase[] {
  const { agent, live, installed, cardUrl, catalogUrl, mcpUrl, liveProducts, cart } = ctx;
  const phases: Phase[] = [
    buildDiscoveryPhase(agent, installed, cardUrl, live.card),
    buildReadMenuPhase(agent, installed, catalogUrl, live.catalog, liveProducts),
  ];

  // ── MCP tools/list — LIVE POST against the merchant's endpoint ──
  const mcpResult = live.mcp;
  const mcpBody = mcpResult ? summarizeToolsList(mcpResult) : '';
  // In sample mode `mcpUrl` is the malformed-key SENTINEL
  // (`…/v1/mcp/invalid`) — never render it; show the placeholder path
  // the other sample rows use. `installed === true` implies a valid
  // key, so the real URL is safe there.
  const mcpDisplayUrl = installed ? mcpUrl : '/v1/mcp/{your_site_key}';
  const mcpShort = mcpDisplayUrl.replace('https://', '');
  phases.push({
    id: 'mcp-tools-list',
    name: 'MCP tools/list',
    tier: 'live',
    narration: installed
      ? mcpResult
        ? mcpResult.ok
          ? (toolNamesFromResult(mcpResult)?.length === 0
              ? `${agent.name} listed the tools your MCP server exposes — 0 tools, live. No skill is live for this site, so agents can discover nothing and buy nothing here (Settings → Skills shows why).`
              : `${agent.name} listed the tools your MCP server exposes — live, in ${mcpResult.ms} ms. This is the discovery step every MCP client runs before any tool call (read-only per the spec).`)
          : `${agent.name} tried to list your MCP tools — the endpoint returned ${mcpResult.status}. The dashboard shows the raw response so you can debug.`
        : `${agent.name} is calling POST tools/list against your MCP endpoint…`
      : `${agent.name} would call POST tools/list against your MCP endpoint here.`,
    endpoint: installed
      ? mcpResult
        ? {
            method: 'POST',
            path: mcpUrl,
            status: mcpResult.status,
            ms: mcpResult.ms,
            body: mcpBody,
            live: true,
          }
        : { method: 'POST', path: mcpUrl, status: 0, ms: 0, body: 'fetching live…', live: true }
      : {
          method: 'POST',
          path: mcpDisplayUrl,
          status: 200,
          ms: 0,
          body: '{ "jsonrpc": "2.0", "id": 1, "result": { "tools": [ … ] } }',
          live: false,
        },
    ok: mcpResult?.ok ?? true,
  });

  // ── Skill-aware gate — grounded in the LIVE tools/list response ──
  // When the merchant has `place_order` disabled, the tool never
  // appears in tools/list and a tools/call would be refused with
  // JSON-RPC -32602 `unknown_tool` before any payment path (no draft
  // enqueued — apps/edge/src/mcp/handler.ts, ADR-0014 §3.2). Render
  // that refusal instead of a make-believe checkout: the journey stays
  // truthful to THIS site's skill policy. `null` tool names (sample
  // mode / fetch pending / unparseable body) keep the illustration —
  // never punish a merchant for a transient fetch state.
  const liveToolNames =
    installed && mcpResult !== undefined && mcpResult.ok
      ? toolNamesFromResult(mcpResult)
      : null;
  const placeOrderExposed =
    liveToolNames === null || liveToolNames.includes('place_order');

  if (!placeOrderExposed) {
    phases.push({
      id: 'mcp-tool-call',
      name: 'Place order',
      tier: 'illustration',
      narration: `Your MCP server doesn't expose place_order — it isn't live for this site, so a Claude order attempt is refused with unknown_tool before any payment path. This is your skill policy working.`,
      endpoint: {
        method: 'POST',
        path: mcpDisplayUrl,
        status: 200,
        ms: 0,
        body: '{ "jsonrpc": "2.0", "id": 2, "error": { "code": -32602, "message": "unknown_tool" } }',
        live: false,
      },
      ok: false,
    });
    phases.push({
      id: 'pay',
      name: 'Payment',
      tier: 'illustration',
      narration: `(skipped)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '(never evaluated)',
        live: false,
      },
      ok: false,
      skipped: true,
    });
    phases.push({
      id: 'fulfill',
      name: 'Fulfillment',
      tier: 'illustration',
      narration: `(skipped)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '(never evaluated)',
        live: false,
      },
      ok: false,
      skipped: true,
    });
    return phases;
  }

  // ── Place order via YOUR MCP tool — ILLUSTRATION ──
  // Place order is a `tools/call` against the merchant's own MCP
  // endpoint (not Anthropic's Cash App connector marketplace). That
  // call carries a Sill-signed mandate; the next phase describes what
  // the mandate pipeline then does.
  phases.push({
    id: 'mcp-tool-call',
    name: 'Place order',
    tier: 'illustration',
    narration: `Claude calls your MCP server's place_order tool. The request carries a Sill-signed mandate — the next step is what Sill does with it.`,
    endpoint: {
      method: 'POST',
      path: mcpDisplayUrl,
      status: 200,
      ms: 0,
      body: cart.unit_minor !== null
        ? `{ "jsonrpc": "2.0", "id": 2, "method": "tools/call", "params": { "name": "place_order", "arguments": { "amount_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} } } }`
        : '{ "jsonrpc": "2.0", "id": 2, "method": "tools/call", "params": { "name": "place_order" } }',
      live: false,
    },
    ok: true,
  });

  // ── Sill-side mandate + settlement + signed audit ILLUSTRATION ──
  phases.push({
    id: 'pay',
    name: 'Payment',
    tier: 'illustration',
    narration: `Sill verifies the signed mandate against your rules, settles via Stripe when your rail is on, and writes a signed audit record. (Illustration — no real charge here.)`,
    endpoint: {
      method: 'POST',
      path: '/v1/m/{site_key}/mandate',
      status: 200,
      ms: 0,
      body: cart.unit_minor !== null
        ? `{ "decision": "approved", "settled": true, "amount_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
        : '{ "decision": "approved", "settled": true }',
      live: false,
    },
    ok: true,
  });

  phases.push({
    id: 'fulfill',
    name: 'Fulfillment',
    tier: 'illustration',
    narration: `Order placed, tracking issued, customer emailed — every step would land in your signed audit log.`,
    endpoint: {
      method: 'POST',
      path: '/v1/m/{site_key}/mandate',
      status: 200,
      ms: 0,
      body: '{ "fulfilled": true, "tracking": "1Z999AA1…" }',
      live: false,
    },
    ok: true,
  });

  return phases;
}
